import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../../../api';
import QuestionFilters from './QuestionFilters';
import QuestionList from './QuestionList';

export default function QuestionsSection({ token, setError }) {
  const location = useLocation();
  const selectedTestId = location.state?.testId;
  const groupSubjectIds = location.state?.subjectIds || [];
  const selectedGroupName = location.state?.groupName || '';
  const selectedGroupId = location.state?.groupId || null;
  const navigate = useNavigate();

  const [questions, setQuestions] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [contexts, setContexts] = useState(null);
  const [tests, setTests] = useState(null);
  const [availableTopics, setAvailableTopics] = useState([]);

  const [qaSearch, setQaSearch] = useState('');
  const [qaTestFilter, setQaTestFilter] = useState(selectedTestId || '');
  const [qaSubjectFilter, setQaSubjectFilter] = useState('');
  const [qaTopicFilter, setQaTopicFilter] = useState('');
  const [qaDifficultyFilter, setQaDifficultyFilter] = useState('');

  const activeTestId = qaTestFilter || selectedTestId;
  const activeTest = tests?.find(t => String(t.id) === String(activeTestId));
  const API_HOST = API_BASE.replace(/\/api\/?$/, '');

  const fetchTopics = async (subjectId) => {
    if (!subjectId) return setAvailableTopics([]);
    try {
      const res = await api.get(`/admin/subjects/${subjectId}/topics`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableTopics(res.data.topics);
    } catch (err) {
      setAvailableTopics([]);
    }
  };

  const fetchData = async () => {
    try {
      const [qres, sres, cres, tres] = await Promise.all([
        api.get('/admin/questions', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/admin/subjects', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/admin/contexts', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/admin/tests', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setQuestions(qres.data.questions);
      setSubjects(sres.data.subjects);
      setContexts(cres.data.contexts);
      setTests(tres.data.tests);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch questions data');
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchData();
  }, [token]);

  useEffect(() => {
    if (!qaSubjectFilter) {
      setAvailableTopics([]);
      setQaTopicFilter('');
      return;
    }
    fetchTopics(qaSubjectFilter);
  }, [qaSubjectFilter]);

  const handleAdd = () => {
    navigate('/questions/add');
  };

  const handleEdit = (q) => {
    navigate(`/questions/edit/${q.id}`);
  };

  const handleDelete = async (q) => {
    const confirmed = window.confirm(`Delete question #${q.id}?`);
    if (!confirmed) return;
    try {
      await api.delete(`/admin/questions/${q.id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete question');
    }
  };

  const loading = !questions || !subjects || !contexts || !tests;

  const filtered = !loading ? questions.filter(q => {
    if (activeTestId && q.test_id != activeTestId) return false;
    if (groupSubjectIds.length > 0 && !groupSubjectIds.map(String).includes(String(q.subject_id))) return false;
    if (qaSubjectFilter && q.subject_id != qaSubjectFilter) return false;
    if (qaTopicFilter && q.topic_id != qaTopicFilter) return false;
    if (qaDifficultyFilter && q.difficulty !== qaDifficultyFilter) return false;
    if (qaSearch && !q.text.toLowerCase().includes(qaSearch.toLowerCase())) return false;
    return true;
  }) : [];

  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-gray-100 py-2 px-1 rounded-t-lg">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Question Bank Management</h2>
          <p className="text-sm text-slate-500">Enterprise-grade question authoring and review dashboard.</p>
          {selectedGroupId && (
            <p className="text-sm text-indigo-700 mt-1">Group filter: <strong>{selectedGroupName}</strong></p>
          )}
        </div>
        {!loading && (
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-600">
              Total questions: <span className="font-semibold text-slate-900">{questions.length}</span>
            </div>
            <div className="text-sm text-slate-600">
              Shown: <span className="font-semibold text-slate-900">{filtered.length}</span>
            </div>
          </div>
        )}
      </div>
      {/* {!loading && (
        // <div className="mb-3 text-sm text-gray-600">Displaying <span className="font-semibold text-gray-800">{filtered.length}</span> of <span className="font-semibold text-gray-800">{questions.length}</span> questions.</div>
      )} */}
      <QuestionFilters
        qaSearch={qaSearch}
        setQaSearch={setQaSearch}
        tests={tests || []}
        qaTestFilter={qaTestFilter}
        setQaTestFilter={setQaTestFilter}
        subjects={subjects || []}
        qaSubjectFilter={qaSubjectFilter}
        setQaSubjectFilter={setQaSubjectFilter}
        availableTopics={availableTopics || []}
        qaTopicFilter={qaTopicFilter}
        setQaTopicFilter={setQaTopicFilter}
        qaDifficultyFilter={qaDifficultyFilter}
        setQaDifficultyFilter={setQaDifficultyFilter}
        activeTest={activeTest}
        onAdd={handleAdd}
      />
      <QuestionList
        filtered={filtered}
        API_HOST={API_HOST}
        token={token}
        setError={setError}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
      />
    </div>
  );
}
